import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { 
  TrendingUp, 
  TrendingDown, 
  ArrowUpRight,
  ArrowDownRight,
  Wallet,
  PieChart,
  History,
  DollarSign,
  Download,
  BarChart3,
  Target,
  Share2,
  Copy,
  Check,
  Link as LinkIcon,
  ShoppingCart,
  Minus,
  Search
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import AppLayout from '../components/AppLayout';
import {
  PieChart as RechartsPieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend
} from 'recharts';

const PortfolioPage = () => {
  const navigate = useNavigate();
  const { api } = useAuth();
  
  const [portfolio, setPortfolio] = useState(null);
  const [positions, setPositions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [comparison, setComparison] = useState(null);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  
  // Buy/Sell dialog state
  const [tradeDialog, setTradeDialog] = useState({ open: false, type: 'buy', symbol: '' });
  const [tradeAmount, setTradeAmount] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [executing, setExecuting] = useState(false);
  
  // Share dialog state
  const [shareDialog, setShareDialog] = useState(false);
  const [sharingSettings, setSharingSettings] = useState(null);
  const [sharePositions, setSharePositions] = useState(true);
  const [shareTransactions, setShareTransactions] = useState(false);
  const [shareValue, setShareValue] = useState(true);
  const [enablingShare, setEnablingShare] = useState(false);
  const [copied, setCopied] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const [portfolioRes, positionsRes, transactionsRes, comparisonRes, sharingRes] = await Promise.all([
        api.get('/portfolio/summary'),
        api.get('/portfolio/positions'),
        api.get('/portfolio/transactions?limit=50'),
        api.get('/portfolio/comparison'),
        api.get('/settings/sharing')
      ]);
      setPortfolio(portfolioRes.data);
      setPositions(positionsRes.data);
      setTransactions(transactionsRes.data);
      setComparison(comparisonRes.data.summary);
      setSharingSettings(sharingRes.data);
    } catch (error) {
      console.error('Failed to fetch portfolio data:', error);
    } finally {
      setLoading(false);
    }
  }, [api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Search stocks for buying
  const handleSearch = async (query) => {
    setSearchQuery(query);
    if (query.length < 1) {
      setSearchResults([]);
      return;
    }
    setSearching(true);
    try {
      const response = await api.get(`/stocks/search?q=${encodeURIComponent(query)}`);
      setSearchResults(response.data.results?.slice(0, 5) || []);
    } catch (error) {
      console.error('Search failed:', error);
    } finally {
      setSearching(false);
    }
  };

  // Execute trade
  const handleTrade = async () => {
    if (!tradeDialog.symbol || !tradeAmount) {
      toast.error('Please enter a stock symbol and amount');
      return;
    }
    
    setExecuting(true);
    try {
      await api.post('/trades/execute', {
        symbol: tradeDialog.symbol.toUpperCase(),
        trade_type: tradeDialog.type,
        amount: parseFloat(tradeAmount)
      });
      toast.success(`${tradeDialog.type === 'buy' ? 'Bought' : 'Sold'} ${tradeDialog.symbol.toUpperCase()} successfully!`);
      setTradeDialog({ open: false, type: 'buy', symbol: '' });
      setTradeAmount('');
      setSearchQuery('');
      setSearchResults([]);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Trade failed');
    } finally {
      setExecuting(false);
    }
  };

  // Portfolio sharing
  const handleEnableSharing = async () => {
    setEnablingShare(true);
    try {
      const response = await api.post(`/settings/sharing/enable?share_positions=${sharePositions}&share_transactions=${shareTransactions}&share_value=${shareValue}`);
      setSharingSettings({ sharing: response.data.settings, share_url: response.data.share_url });
      toast.success('Portfolio sharing enabled!');
    } catch (error) {
      toast.error('Failed to enable sharing');
    } finally {
      setEnablingShare(false);
    }
  };

  const handleDisableSharing = async () => {
    try {
      await api.post('/settings/sharing/disable');
      setSharingSettings({ sharing: { enabled: false }, share_url: null });
      toast.success('Portfolio sharing disabled');
    } catch (error) {
      toast.error('Failed to disable sharing');
    }
  };

  const handleUpdateSharing = async () => {
    try {
      await api.put(`/settings/sharing?share_positions=${sharePositions}&share_transactions=${shareTransactions}&share_value=${shareValue}`);
      toast.success('Sharing settings updated');
    } catch (error) {
      toast.error('Failed to update sharing settings');
    }
  };

  const copyShareLink = () => {
    const fullUrl = `${window.location.origin}${sharingSettings?.share_url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const response = await api.get('/portfolio/export', {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `virtuinvest_transactions_${new Date().toISOString().slice(0, 10)}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Transactions exported successfully!');
    } catch (error) {
      toast.error('Failed to export transactions');
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2
    }).format(value);
  };

  const formatPercent = (value) => {
    const prefix = value >= 0 ? '+' : '';
    return `${prefix}${value?.toFixed(2) || '0.00'}%`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Pie chart data
  const pieData = positions.map((pos, index) => ({
    name: pos.symbol,
    value: pos.market_value,
    color: ['#1A4D2E', '#C1F23D', '#10B981', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6'][index % 7]
  }));

  // Add cash to pie chart if significant
  if (portfolio?.cash_balance > 0) {
    pieData.push({
      name: 'Cash',
      value: portfolio.cash_balance,
      color: '#9CA3AF'
    });
  }

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="custom-tooltip">
          <p className="font-semibold text-[#1F2937]">{payload[0].name}</p>
          <p className="text-sm text-[#1A4D2E]">{formatCurrency(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-[60vh]">
          <div className="animate-pulse-subtle text-primary font-serif text-xl">Loading portfolio...</div>
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="space-y-8" data-testid="portfolio-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="font-serif text-3xl font-semibold text-[#1F2937]">Portfolio</h1>
            <p className="text-gray-500 mt-1">Track your positions and performance</p>
          </div>
          <div className="flex items-center gap-3">
            {/* Buy Stock Button */}
            <Dialog open={tradeDialog.open && tradeDialog.type === 'buy'} onOpenChange={(open) => setTradeDialog({ ...tradeDialog, open, type: 'buy' })}>
              <DialogTrigger asChild>
                <Button 
                  className="bg-[#1A4D2E] hover:bg-[#143d24] text-white"
                  data-testid="buy-stock-btn"
                >
                  <ShoppingCart className="w-4 h-4 mr-2" />
                  Buy Stock
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-serif">Buy Stock</DialogTitle>
                  <DialogDescription>
                    Search for a stock and enter the amount you want to invest.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  <div className="space-y-2">
                    <Label>Search Stock</Label>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                      <Input
                        placeholder="Search by symbol or name..."
                        value={searchQuery}
                        onChange={(e) => handleSearch(e.target.value)}
                        className="pl-10"
                      />
                    </div>
                    {searchResults.length > 0 && (
                      <div className="border rounded-lg max-h-40 overflow-y-auto">
                        {searchResults.map((stock) => (
                          <button
                            key={stock.symbol}
                            onClick={() => {
                              setTradeDialog({ ...tradeDialog, symbol: stock.symbol });
                              setSearchQuery(stock.symbol);
                              setSearchResults([]);
                            }}
                            className="w-full px-3 py-2 text-left hover:bg-green-50 flex justify-between items-center"
                          >
                            <span className="font-medium">{stock.symbol}</span>
                            <span className="text-sm text-gray-500 truncate ml-2">{stock.description}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {tradeDialog.symbol && (
                    <div className="space-y-2">
                      <Label>Amount ($)</Label>
                      <Input
                        type="number"
                        placeholder="Enter amount to invest"
                        value={tradeAmount}
                        onChange={(e) => setTradeAmount(e.target.value)}
                        min="1"
                      />
                      <p className="text-xs text-gray-500">
                        Available: {formatCurrency(portfolio?.cash_balance || 0)}
                      </p>
                    </div>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTradeDialog({ open: false, type: 'buy', symbol: '' })}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleTrade} 
                    disabled={!tradeDialog.symbol || !tradeAmount || executing}
                    className="bg-[#1A4D2E] hover:bg-[#143d24]"
                  >
                    {executing ? 'Processing...' : `Buy ${tradeDialog.symbol || 'Stock'}`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Sell Stock Button */}
            <Dialog open={tradeDialog.open && tradeDialog.type === 'sell'} onOpenChange={(open) => setTradeDialog({ ...tradeDialog, open, type: 'sell' })}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-red-200 text-red-600 hover:bg-red-50"
                  data-testid="sell-stock-btn"
                >
                  <Minus className="w-4 h-4 mr-2" />
                  Sell Stock
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-serif">Sell Stock</DialogTitle>
                  <DialogDescription>
                    Select a position to sell from your portfolio.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {positions.length === 0 ? (
                    <p className="text-center text-gray-500 py-4">No positions to sell</p>
                  ) : (
                    <>
                      <div className="space-y-2">
                        <Label>Select Stock</Label>
                        <div className="border rounded-lg max-h-40 overflow-y-auto">
                          {positions.map((pos) => (
                            <button
                              key={pos.symbol}
                              onClick={() => setTradeDialog({ ...tradeDialog, symbol: pos.symbol })}
                              className={`w-full px-3 py-2 text-left hover:bg-red-50 flex justify-between items-center ${
                                tradeDialog.symbol === pos.symbol ? 'bg-red-50' : ''
                              }`}
                            >
                              <span className="font-medium">{pos.symbol}</span>
                              <span className="text-sm text-gray-500">
                                {pos.shares.toFixed(2)} shares • {formatCurrency(pos.market_value)}
                              </span>
                            </button>
                          ))}
                        </div>
                      </div>
                      {tradeDialog.symbol && (
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <Label>Amount ($) to Sell</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              className="h-7 px-2 text-xs border-red-200 text-red-600 hover:bg-red-50"
                              onClick={() => {
                                const pos = positions.find(p => p.symbol === tradeDialog.symbol);
                                if (pos) setTradeAmount(pos.market_value.toFixed(2));
                              }}
                              data-testid="sell-all-btn"
                            >
                              Sell All
                            </Button>
                          </div>
                          <Input
                            type="number"
                            placeholder="Enter amount to sell"
                            value={tradeAmount}
                            onChange={(e) => setTradeAmount(e.target.value)}
                            min="1"
                          />
                          <p className="text-xs text-gray-500">
                            Position value: {formatCurrency(positions.find(p => p.symbol === tradeDialog.symbol)?.market_value || 0)}
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setTradeDialog({ open: false, type: 'sell', symbol: '' })}>
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleTrade} 
                    disabled={!tradeDialog.symbol || !tradeAmount || executing}
                    className="bg-red-600 hover:bg-red-700 text-white"
                  >
                    {executing ? 'Processing...' : `Sell ${tradeDialog.symbol || 'Stock'}`}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            {/* Share Button */}
            <Dialog open={shareDialog} onOpenChange={setShareDialog}>
              <DialogTrigger asChild>
                <Button 
                  variant="outline"
                  className="border-stone-200"
                  data-testid="share-portfolio-btn"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle className="font-serif">Share Your Portfolio</DialogTitle>
                  <DialogDescription>
                    Create a public link to share your portfolio performance.
                  </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 py-4">
                  {sharingSettings?.sharing?.enabled ? (
                    <>
                      {/* Share Link */}
                      <div className="p-3 bg-green-50 rounded-lg border border-green-100">
                        <p className="text-sm text-green-800 mb-2">Your portfolio is being shared!</p>
                        <div className="flex items-center gap-2">
                          <Input 
                            readOnly 
                            value={`${window.location.origin}${sharingSettings.share_url}`}
                            className="text-sm"
                          />
                          <Button size="sm" onClick={copyShareLink} variant="outline">
                            {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                          </Button>
                        </div>
                      </div>
                      
                      {/* Sharing Options */}
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-gray-700">What to share:</p>
                        <div className="flex items-center justify-between">
                          <Label htmlFor="share-value" className="text-sm">Portfolio Value & P&L</Label>
                          <Switch 
                            id="share-value"
                            checked={shareValue}
                            onCheckedChange={(v) => { setShareValue(v); handleUpdateSharing(); }}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label htmlFor="share-positions" className="text-sm">Positions (holdings)</Label>
                          <Switch 
                            id="share-positions"
                            checked={sharePositions}
                            onCheckedChange={(v) => { setSharePositions(v); handleUpdateSharing(); }}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <Label htmlFor="share-transactions" className="text-sm">Transaction History</Label>
                          <Switch 
                            id="share-transactions"
                            checked={shareTransactions}
                            onCheckedChange={(v) => { setShareTransactions(v); handleUpdateSharing(); }}
                          />
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="space-y-3">
                        <p className="text-sm font-medium text-gray-700">Choose what to share:</p>
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="share-value-new" className="text-sm">Portfolio Value & P&L</Label>
                            <p className="text-xs text-gray-500">Total value and profit/loss</p>
                          </div>
                          <Switch 
                            id="share-value-new"
                            checked={shareValue}
                            onCheckedChange={setShareValue}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="share-positions-new" className="text-sm">Positions</Label>
                            <p className="text-xs text-gray-500">Your stock holdings</p>
                          </div>
                          <Switch 
                            id="share-positions-new"
                            checked={sharePositions}
                            onCheckedChange={setSharePositions}
                          />
                        </div>
                        <div className="flex items-center justify-between">
                          <div>
                            <Label htmlFor="share-transactions-new" className="text-sm">Transactions</Label>
                            <p className="text-xs text-gray-500">Recent buy/sell history</p>
                          </div>
                          <Switch 
                            id="share-transactions-new"
                            checked={shareTransactions}
                            onCheckedChange={setShareTransactions}
                          />
                        </div>
                      </div>
                    </>
                  )}
                </div>
                <DialogFooter>
                  {sharingSettings?.sharing?.enabled ? (
                    <Button variant="destructive" onClick={handleDisableSharing}>
                      Stop Sharing
                    </Button>
                  ) : (
                    <Button 
                      onClick={handleEnableSharing}
                      disabled={enablingShare}
                      className="bg-[#1A4D2E] hover:bg-[#143d24]"
                    >
                      {enablingShare ? 'Creating Link...' : 'Create Share Link'}
                    </Button>
                  )}
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total Equity</p>
                  <p className="text-2xl font-bold text-[#1F2937] tabular-nums mt-1" data-testid="portfolio-equity">
                    {formatCurrency(portfolio?.total_equity || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-green-50 rounded-xl flex items-center justify-center">
                  <Wallet className="w-6 h-6 text-[#1A4D2E]" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Invested</p>
                  <p className="text-2xl font-bold text-[#1F2937] tabular-nums mt-1">
                    {formatCurrency(portfolio?.invested_value || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center">
                  <PieChart className="w-6 h-6 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Cash Available</p>
                  <p className="text-2xl font-bold text-[#1F2937] tabular-nums mt-1">
                    {formatCurrency(portfolio?.cash_balance || 0)}
                  </p>
                </div>
                <div className="w-12 h-12 bg-yellow-50 rounded-xl flex items-center justify-center">
                  <DollarSign className="w-6 h-6 text-yellow-600" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="bg-white border-stone-100 shadow-card">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wider text-gray-500 font-medium">Total P&L</p>
                  <p className={`text-2xl font-bold tabular-nums mt-1 ${(portfolio?.total_pl || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {formatCurrency(portfolio?.total_pl || 0)}
                  </p>
                  <p className={`text-sm ${(portfolio?.total_pl_percent || 0) >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {formatPercent(portfolio?.total_pl_percent || 0)}
                  </p>
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${(portfolio?.total_pl || 0) >= 0 ? 'bg-green-50' : 'bg-red-50'}`}>
                  {(portfolio?.total_pl || 0) >= 0 ? (
                    <ArrowUpRight className="w-6 h-6 text-green-600" />
                  ) : (
                    <ArrowDownRight className="w-6 h-6 text-red-500" />
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Benchmark Comparison */}
        {comparison && (
          <Card className="bg-white border-stone-100 shadow-card">
            <CardHeader className="pb-2">
              <CardTitle className="font-serif text-xl text-[#1F2937] flex items-center gap-2">
                <Target className="w-5 h-5" />
                Performance vs {comparison.benchmark}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-3 gap-6">
                <div className="text-center p-4 bg-stone-50 rounded-xl">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Your Return</p>
                  <p className={`text-2xl font-bold ${comparison.portfolio_return >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {formatPercent(comparison.portfolio_return)}
                  </p>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-xl">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">S&P 500</p>
                  <p className={`text-2xl font-bold ${comparison.spy_return >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {formatPercent(comparison.spy_return)}
                  </p>
                </div>
                <div className="text-center p-4 bg-stone-50 rounded-xl">
                  <p className="text-xs uppercase tracking-wider text-gray-500 mb-1">Difference</p>
                  <p className={`text-2xl font-bold ${comparison.difference >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {comparison.difference >= 0 ? '+' : ''}{comparison.difference.toFixed(2)}%
                  </p>
                  <p className="text-xs text-gray-500 mt-1">
                    {comparison.difference >= 0 ? 'Beating the market!' : 'Underperforming'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Main Content */}
        <Tabs defaultValue="positions" className="space-y-6">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="positions" className="data-[state=active]:bg-white">
              <PieChart className="w-4 h-4 mr-2" />
              Positions
            </TabsTrigger>
            <TabsTrigger value="transactions" className="data-[state=active]:bg-white">
              <History className="w-4 h-4 mr-2" />
              Transactions
            </TabsTrigger>
          </TabsList>

          {/* Positions Tab */}
          <TabsContent value="positions">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Positions Table */}
              <Card className="lg:col-span-2 bg-white border-stone-100 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-[#1F2937]">Holdings</CardTitle>
                </CardHeader>
                <CardContent>
                  {positions.length === 0 ? (
                    <div className="text-center py-12">
                      <PieChart className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                      <p className="text-gray-500 mb-2">No positions yet</p>
                      <p className="text-sm text-gray-400 mb-4">Start by buying some stocks</p>
                      <Button 
                        onClick={() => navigate('/dashboard')}
                        className="bg-[#1A4D2E] hover:bg-[#143d24]"
                      >
                        Browse Stocks
                      </Button>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full">
                        <thead>
                          <tr className="text-left border-b border-stone-100">
                            <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Symbol</th>
                            <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Shares</th>
                            <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Avg Cost</th>
                            <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Current</th>
                            <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Value</th>
                            <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold text-right">P&L</th>
                          </tr>
                        </thead>
                        <tbody>
                          {positions.map((pos) => (
                            <tr 
                              key={pos.symbol} 
                              className="border-b border-stone-50 hover:bg-green-50/30 cursor-pointer transition-colors"
                              onClick={() => navigate(`/stock/${pos.symbol}`)}
                              data-testid={`position-${pos.symbol}`}
                            >
                              <td className="py-4">
                                <p className="font-semibold text-[#1F2937]">{pos.symbol}</p>
                              </td>
                              <td className="py-4">
                                <p className="tabular-nums">{pos.shares.toFixed(4)}</p>
                              </td>
                              <td className="py-4">
                                <p className="tabular-nums">{formatCurrency(pos.avg_cost)}</p>
                              </td>
                              <td className="py-4">
                                <p className="tabular-nums">{formatCurrency(pos.current_price)}</p>
                              </td>
                              <td className="py-4">
                                <p className="font-medium tabular-nums">{formatCurrency(pos.market_value)}</p>
                              </td>
                              <td className="py-4 text-right">
                                <div className={`flex items-center justify-end gap-1 ${pos.unrealized_pl >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                                  {pos.unrealized_pl >= 0 ? (
                                    <TrendingUp className="w-4 h-4" />
                                  ) : (
                                    <TrendingDown className="w-4 h-4" />
                                  )}
                                  <div className="text-right">
                                    <p className="font-medium tabular-nums">{formatCurrency(pos.unrealized_pl)}</p>
                                    <p className="text-xs tabular-nums">{formatPercent(pos.unrealized_pl_percent)}</p>
                                  </div>
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Allocation Chart */}
              <Card className="bg-white border-stone-100 shadow-card">
                <CardHeader>
                  <CardTitle className="font-serif text-xl text-[#1F2937]">Allocation</CardTitle>
                </CardHeader>
                <CardContent>
                  {pieData.length > 0 ? (
                    <div className="h-64">
                      <ResponsiveContainer width="100%" height="100%">
                        <RechartsPieChart>
                          <Pie
                            data={pieData}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={80}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {pieData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} />
                            ))}
                          </Pie>
                          <Tooltip content={<CustomTooltip />} />
                          <Legend 
                            formatter={(value) => <span className="text-sm text-gray-600">{value}</span>}
                          />
                        </RechartsPieChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div className="h-64 flex items-center justify-center text-gray-500">
                      No allocation data
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Transactions Tab */}
          <TabsContent value="transactions">
            <Card className="bg-white border-stone-100 shadow-card">
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="font-serif text-xl text-[#1F2937]">Transaction History</CardTitle>
                  {transactions.length > 0 && (
                    <Button
                      variant="outline"
                      onClick={handleExport}
                      disabled={exporting}
                      className="border-[#1A4D2E]/20 text-[#1A4D2E] hover:bg-green-50"
                      data-testid="export-csv-btn"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {exporting ? 'Exporting...' : 'Export CSV'}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                {transactions.length === 0 ? (
                  <div className="text-center py-12">
                    <History className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 mb-2">No transactions yet</p>
                    <p className="text-sm text-gray-400">Your trades will appear here</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="text-left border-b border-stone-100">
                          <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Date</th>
                          <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Type</th>
                          <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Symbol</th>
                          <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Shares</th>
                          <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold">Price</th>
                          <th className="pb-3 text-xs uppercase tracking-wider text-gray-500 font-semibold text-right">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {transactions.map((tx) => (
                          <tr 
                            key={tx.id} 
                            className="border-b border-stone-50"
                            data-testid={`transaction-${tx.id}`}
                          >
                            <td className="py-4">
                              <p className="text-sm text-gray-500">{formatDate(tx.executed_at)}</p>
                            </td>
                            <td className="py-4">
                              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                tx.trade_type === 'buy' 
                                  ? 'bg-green-100 text-green-800' 
                                  : 'bg-red-100 text-red-800'
                              }`}>
                                {tx.trade_type.toUpperCase()}
                              </span>
                            </td>
                            <td className="py-4">
                              <p className="font-semibold text-[#1F2937]">{tx.symbol}</p>
                            </td>
                            <td className="py-4">
                              <p className="tabular-nums">{tx.shares.toFixed(4)}</p>
                            </td>
                            <td className="py-4">
                              <p className="tabular-nums">{formatCurrency(tx.price)}</p>
                            </td>
                            <td className="py-4 text-right">
                              <p className={`font-medium tabular-nums ${tx.trade_type === 'buy' ? 'text-red-500' : 'text-green-600'}`}>
                                {tx.trade_type === 'buy' ? '-' : '+'}{formatCurrency(tx.total)}
                              </p>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default PortfolioPage;
